/* eslint-disable */
import { useEffect, useState } from "react";
import { Link, useParams, useLocation, Navigate } from "react-router-dom";
import api from "../lib/api";
import FeedbackPrompt from "../components/FeedbackPrompt";
import EventCard from "../components/EventCard";
import { trackPartnerClick } from "../lib/clickTrack";
import Img from "../components/Img";

const REGION_META = {
  nh: {
    slug: "nh",
    label: "New Hampshire",
    stateLabel: "New Hampshire",
    eyebrow: "Métier · New Hampshire activation",
    h1: <>A boutique professional network <em>for New Hampshire</em>.</>,
    desc: (flagship) => (
      <>Curated coworking spaces from Portsmouth to Manchester, anchored by <strong>{flagship?.name || "HRKNSScowork"}</strong> in Concord. One free Métier membership unlocks the entire network, with Boston &amp; Cambridge on deck via the <Link to="/ma" style={{color:"var(--gold)", textDecoration:"underline"}}>Massachusetts directory</Link>.</>
    ),
    coverage: "Concord · Portsmouth · Manchester",
    flagshipEyebrow: "FLAGSHIP",
  },
  ma: {
    slug: "ma",
    label: "Massachusetts",
    eyebrow: "Métier · MA activation",
    h1: <>A boutique professional network <em>for Massachusetts</em>.</>,
    desc: () => (
      <>Curated boutique workspaces across Boston and Cambridge. One free Métier membership unlocks the entire network.</>
    ),
    coverage: "Boston · Cambridge",
    flagshipEyebrow: "FLAGSHIP",
  },
};

function PublicHeader({ regionLabel }) {
  return (
    <header className="rec-topbar">
      <Link to="/spaces" className="rec-brand" data-testid="region-brand">
        <span className="rec-brand-mark"><img src="/brand/metier-icon.png" alt="Métier" /></span>
      </Link>
      <nav className="rec-topnav">
        <a href="#spaces">Spaces</a>
        <Link to="/hours">Métier Hours</Link>
        <Link to="/list-your-space">List your space</Link>
        <Link to="/login" data-testid="region-signin">Member sign in</Link>
      </nav>
    </header>
  );
}

function fmt(n) {
  if (n == null || n === 0) return null;
  return `$${Number(n).toFixed(0)}`;
}

// Booking-type → CTA copy + disclosure copy. Keep in sync with backend PartnerClickIn.
const BOOKING_TYPE_META = {
  external_direct_booking: {
    cta: "View availability & book →",
    disclosure: "Booking completed on the workspace's reservation partner.",
  },
  external_inquiry: {
    cta: "Inquire about availability →",
    disclosure: "Availability requests are completed directly with the workspace.",
  },
  metier_checkout: {
    cta: "Book with Métier →",
    disclosure: null,
  },
};
function ctaFor(bookingType) {
  return BOOKING_TYPE_META[bookingType] || BOOKING_TYPE_META.external_direct_booking;
}
// Per-workspace CTA overrides take precedence over the booking-type default (e.g. Flexhub).
// HRKNSScowork's LiquidSpace listing shows the parent studio (not a single room),
// so we surface the partner name in the CTA to set the right expectation.
const SLUG_CTA_OVERRIDES = {
  hrknsscowork: "View availability on LiquidSpace →",
};
function ctaTextFor(s, bookingType) {
  if (s && s.booking_cta) return s.booking_cta;
  if (s && s.slug && SLUG_CTA_OVERRIDES[s.slug]) return SLUG_CTA_OVERRIDES[s.slug];
  return ctaFor(bookingType).cta;
}
// Cove uses its own disclosure copy (workspace-specific, per policy).
function disclosureFor(slug, bookingType) {
  if (slug === "cove-portsmouth") {
    return "Availability requests are completed directly with COVE.";
  }
  return ctaFor(bookingType).disclosure;
}

function Hero({ hero, regionSlug }) {
  if (!hero) return null;
  const heroImg = hero.img || (hero.gallery && hero.gallery[0]) || null;
  const isExternal = Boolean((hero.external_booking || hero.external_booking_url) && (hero.booking_url || hero.external_booking_url));
  const bookingType = hero.booking_type || (isExternal ? "external_direct_booking" : null);
  const rawUrl = hero.external_booking_url || hero.booking_url;
  const ctaText = ctaTextFor(hero, bookingType);
  const disclosure = disclosureFor(hero.slug, bookingType);
  const handleExternal = () => {
    // Fire-and-forget tracking, does NOT block the anchor navigation.
    // The `<a href target="_blank" rel="noopener noreferrer">` handles the
    // new-tab open natively, and its href already contains the UTM-tagged URL.
    trackPartnerClick({
      slug: hero.slug,
      name: hero.name,
      city: hero.city,
      source: regionSlug,
      fallbackUrl: rawUrl,
      bookingType,
      pageSource: `/${regionSlug}`,
    });
  };
  return (
    <section className="dmv-hero">
      <div className="dmv-hero-left">
        <span className="rec-eyebrow">FLAGSHIP</span>
        <h2 className="rec-h2">{hero.name}</h2>
        {hero.address && <p className="dmv-hero-address">{hero.address}</p>}
        <p className="dmv-hero-loc">{hero.neighborhood} · {hero.city} · {hero.type}</p>
        {hero.description && <p className="rec-desc" style={{maxWidth:540}}>{hero.description}</p>}
        {hero.rates && (fmt(hero.rates.hourly) || fmt(hero.rates.daily)) && (
          <div className="dmv-hero-card-rates" style={{marginTop:14}}>
            {fmt(hero.rates.hourly) && <strong>{fmt(hero.rates.hourly)}<span>/hour</span></strong>}
            {fmt(hero.rates.daily) && <strong>{fmt(hero.rates.daily)}<span>/day</span></strong>}
          </div>
        )}
        <div style={{marginTop:18, display:"flex", gap:10, flexWrap:"wrap"}}>
          {isExternal ? (
            <a
              href={rawUrl}
              onClick={handleExternal}
              target="_blank"
              rel="noopener noreferrer"
              className="m-btn-gold"
              data-testid={`region-hero-book-${hero.slug}`}
            >{ctaText}</a>
          ) : (
            <Link to={`/spaces/${hero.slug}`} className="m-btn-gold">View space →</Link>
          )}
          <a href="#spaces" className="m-btn-ghost">Browse directory</a>
        </div>
        {isExternal && disclosure && (
          <p className="dmv-hero-disclosure" data-testid={`region-hero-disclosure-${hero.slug}`}>{disclosure}</p>
        )}
      </div>
      {heroImg && (
        <div className="dmv-hero-right">
          <img src={heroImg} alt={`${hero.name}, ${hero.neighborhood ? hero.neighborhood + ', ' : ''}${hero.city || ''}`} loading="lazy" />
        </div>
      )}
    </section>
  );
}

function SpaceCard({ s, regionSlug, requestCount, alreadyRequested, onRequested }) {
  const isExternal = Boolean((s.external_booking || s.external_booking_url) && (s.booking_url || s.external_booking_url));
  const bookingType = s.booking_type || (isExternal ? "external_direct_booking" : null);
  const rawUrl = s.external_booking_url || s.booking_url;
  const ctaText = ctaTextFor(s, bookingType);
  const disclosure = disclosureFor(s.slug, bookingType);
  const [busy, setBusy] = useState(false);
  const [localReq, setLocalReq] = useState({
    count: requestCount || 0,
    mine: !!alreadyRequested,
  });

  // Keep card state in sync when parent counts refresh.
  useEffect(() => {
    setLocalReq({ count: requestCount || 0, mine: !!alreadyRequested });
  }, [requestCount, alreadyRequested]);

  const handleExternal = () => {
    trackPartnerClick({
      slug: s.slug,
      name: s.name,
      city: s.city,
      source: regionSlug,
      fallbackUrl: rawUrl,
      bookingType,
      pageSource: `/${regionSlug}`,
    });
  };
  const handleRequest = async (e) => {
    e.preventDefault();
    if (busy || localReq.mine) return;
    setBusy(true);
    try {
      const { data } = await api.post("/workspace-requests", {
        workspace_slug: s.slug,
        workspace_name: s.name,
        workspace_city: s.city,
        workspace_source: `${regionSlug}_activation`,
      });
      setLocalReq({ count: data.count, mine: true });
      onRequested && onRequested(s.slug, data.count);
    } catch { /* silently ignore, button just doesn't tick */ }
    setBusy(false);
  };

  return (
    <article className="dmv-space-card" data-testid={`region-space-${s.slug}`}>
      {isExternal ? (
        <a href={rawUrl} onClick={handleExternal} target="_blank" rel="noopener noreferrer" className="dmv-space-img-wrap">
          {s.img || (s.gallery && s.gallery[0]) ? (
            <Img src={s.img || s.gallery?.[0]} alt={`${s.name}, ${s.neighborhood ? s.neighborhood + ', ' : ''}${s.city || ''}`} />
          ) : <div className="dmv-space-img-placeholder" />}
          {s.today && <span className="dmv-badge">Available today</span>}
        </a>
      ) : (
        <Link to={`/spaces/${s.slug}`} className="dmv-space-img-wrap">
          {s.img || (s.gallery && s.gallery[0]) ? (
            <Img src={s.img || s.gallery?.[0]} alt={`${s.name}, ${s.neighborhood ? s.neighborhood + ', ' : ''}${s.city || ''}`} />
          ) : <div className="dmv-space-img-placeholder" />}
          {s.today && <span className="dmv-badge">Available today</span>}
        </Link>
      )}
      <div className="dmv-space-body">
        <h3 className="dmv-space-title">{s.name}</h3>
        <p className="dmv-space-loc">{s.neighborhood} · {s.city}</p>
        <p className="dmv-space-type">{s.type}</p>
        {!isExternal && s.rates && (
          <div className="dmv-space-rates">
            {fmt(s.rates.hourly) && <span>{fmt(s.rates.hourly)}<small>/hour</small></span>}
            {fmt(s.rates.daily) && <span>{fmt(s.rates.daily)}<small>/day</small></span>}
          </div>
        )}
        {isExternal ? (
          <>
            <a
              href={rawUrl}
              onClick={handleExternal}
              target="_blank"
              rel="noopener noreferrer"
              className={"m-btn-ghost dmv-space-cta " + (bookingType === "external_inquiry" ? "is-inquiry" : "is-booking")}
              data-testid={`region-book-${s.slug}`}
              data-booking-type={bookingType}
            >{ctaText}</a>
            {disclosure && (
              <p className="dmv-space-disclosure" data-testid={`region-disclosure-${s.slug}`}>{disclosure}</p>
            )}
            <button
              type="button"
              onClick={handleRequest}
              disabled={busy || localReq.mine}
              className={`m-request-btn ${localReq.mine ? "done" : ""}`}
              data-testid={`region-request-${s.slug}`}
              title={localReq.mine ? "You've already requested this workspace" : "Ask Métier to add this space as an official partner"}
            >
              {localReq.mine ? "✓ Requested" : (busy ? "Sending…" : "Request as Métier partner")}
              {localReq.count > 0 && (
                <span className="m-request-count" data-testid={`region-request-count-${s.slug}`}>
                  · {localReq.count} {localReq.count === 1 ? "request" : "requests"}
                </span>
              )}
            </button>
          </>
        ) : (
          <Link to={`/spaces/${s.slug}`} className="m-btn-ghost dmv-space-cta" data-testid={`region-view-${s.slug}`}>View space →</Link>
        )}
      </div>
    </article>
  );
}

function CafeStrip({ cafes, regionLabel, regionSlug }) {
  if (!cafes || cafes.length === 0) return null;
  return (
    <section className="dmv-cafes" id="cafes" data-testid={`region-cafe-strip-${regionSlug}`}>
      <span className="rec-eyebrow">Métier Hours · {regionLabel}</span>
      <h2 className="rec-h2">Work-friendly <em>cafés</em> across {regionLabel}.</h2>
      <p className="rec-desc" style={{maxWidth:640}}>
        Hand-picked cafés where Métier members get a reliable WiFi seat, an outlet, and a perfectly fine espresso. No bookings, just go.
      </p>
      <div className="dmv-cafe-grid">
        {cafes.slice(0, 6).map(c => (
          <a key={c.name}
             href={c.url}
             target="_blank"
             rel="noopener noreferrer"
             className="dmv-cafe-card"
             data-testid={`region-cafe-${(c.name||'').replace(/\s+/g,'-').toLowerCase()}`}>
            <div className="dmv-cafe-name">{c.name}</div>
            <div className="dmv-cafe-meta">{c.neighborhood}</div>
            <div className="dmv-cafe-meta small">{c.hours}</div>
          </a>
        ))}
      </div>
      <div style={{ marginTop: 18 }}>
        <Link to="/hours" className="m-btn-ghost">View all Métier Hours cafés →</Link>
      </div>
    </section>
  );
}

export default function Region() {
  const params = useParams();
  const location = useLocation();
  const pathSlug = (location.pathname || "").replace(/^\/+/, "").split("/")[0];
  const slug = ((params.region || pathSlug) || "").toLowerCase();
  const meta = REGION_META[slug];
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [err, setErr] = useState("");
  const [requestCounts, setRequestCounts] = useState({});
  const [upcomingEvents, setUpcomingEvents] = useState([]);

  useEffect(() => {
    if (!meta) return;
    api.get(`/regions/${slug}/spaces`)
      .then(r => setData(r.data))
      .catch(e => setErr(e?.response?.data?.detail || e.message || "Failed to load"))
      .finally(() => setLoading(false));
    api.get("/workspace-requests/counts")
      .then(r => setRequestCounts(r.data || {}))
      .catch(() => {});
    // Fetch published events for this region, first state in the region's set is used as the city filter.
    const cityForRegion = { dmv: "Washington DC", nh: "New Hampshire", ma: "Massachusetts" }[slug];
    if (cityForRegion) {
      api.get("/events/local", { params: { city: cityForRegion, limit: 6 } })
        .then(r => setUpcomingEvents(r.data || []))
        .catch(() => setUpcomingEvents([]));
    }
  }, [slug]); // eslint-disable-line

  if (!meta || slug === "dmv") return <Navigate to="/nh" replace />;

  const handleRequested = (slugKey, newCount) => {
    setRequestCounts(prev => ({ ...prev, [slugKey]: newCount }));
  };

  // Post-external-click return prompt: if the user just left the site via a
  // partner card and came back within 30 minutes, prompt them "How did it go?".
  const [returnPrompt, setReturnPrompt] = useState(null);
  useEffect(() => {
    try {
      const raw = sessionStorage.getItem("metier_pending_click_feedback");
      if (!raw) return;
      const m = JSON.parse(raw);
      if (!m || !m.slug) return;
      const ageMs = Date.now() - (m.at || 0);
      if (ageMs < 30 * 1000) return;  // give them a beat to actually browse
      if (ageMs > 30 * 60 * 1000) {
        sessionStorage.removeItem("metier_pending_click_feedback");
        return;
      }
      setReturnPrompt(m);
    } catch { /* ignore */ }
  }, [data]);
  const dismissReturnPrompt = () => {
    try { sessionStorage.removeItem("metier_pending_click_feedback"); } catch {}
    setReturnPrompt(null);
  };

  return (
    <div className="rec-page dmv-page">
      <PublicHeader regionLabel={meta.label} />
      <main className="rec-main dmv-main">
        {loading && <div className="m-loading-inline">Loading {meta.label} directory…</div>}
        {err && <div className="m-empty">{err}</div>}

        {data && (
          <>
            <section className="dmv-intro" data-testid="region-intro">
              <span className="rec-eyebrow">{meta.eyebrow}</span>
              <h1 className="rec-h1" style={{maxWidth:900}}>{meta.h1}</h1>
              <p className="rec-desc" style={{maxWidth:680}}>{meta.desc(data.hero)}</p>
              <div className="dmv-intro-stats" style={{marginTop:24}}>
                <div><span className="dmv-hero-num">Curated</span><small>BOUTIQUE NETWORK</small></div>
                <div><span className="dmv-hero-num">{meta.coverage}</span><small>{meta.label.toUpperCase()} COVERAGE</small></div>
                <div><span className="dmv-hero-num">Free</span><small>MEMBERSHIP</small></div>
              </div>
            </section>

            {data.hero && <Hero hero={data.hero} regionSlug={meta.slug} />}

            <CafeStrip cafes={data.cafes} regionLabel={meta.label} regionSlug={meta.slug} />

            <section className="dmv-spaces-section" id="spaces">
              <span className="rec-eyebrow">{meta.label} directory</span>
              <h2 className="rec-h2">Every Métier workspace <em>in {meta.label}</em>.</h2>
              <div className="dmv-space-grid">
                {data.spaces.length === 0 ? (
                  <div className="m-empty">No additional listings yet, flagship is up top.</div>
                ) : (
                  data.spaces.map(s => (
                    <SpaceCard
                      key={s.slug}
                      s={s}
                      regionSlug={meta.slug}
                      requestCount={requestCounts[s.slug] || 0}
                      alreadyRequested={false}
                      onRequested={handleRequested}
                    />
                  ))
                )}
              </div>
            </section>

            <section className="dmv-community" id="community">
              <span className="rec-eyebrow">Métier Common · {meta.label}</span>
              <h2 className="rec-h2">The <em>{meta.label}</em> conversation.</h2>
              <p className="rec-desc" style={{maxWidth:600}}>
                Métier Common is the quiet, members-only feed for the Métier network. Members can share what they&apos;re working on, ask for a recommendation, or post a dispatch from their desk.
              </p>
              <Link to="/community" className="m-btn-ghost" style={{marginTop:14, display:"inline-block"}}>Open Métier Common →</Link>
            </section>

            <section className="dmv-cta">
              <h2 className="rec-h2">Join the <em>Métier</em> network.</h2>
              <p className="rec-desc" style={{maxWidth:560}}>
                One free membership opens every workspace and every Métier Hours café perk across the network.
              </p>
              <Link to="/signup" className="m-btn-gold m-region-join-cta" data-testid="region-cta-signup">Join network →</Link>
            </section>
          </>
        )}
        {upcomingEvents.length > 0 && (
          <section className="m-region-events" data-testid="region-events">
            <span className="m-eyebrow" style={{color:"var(--gold)"}}>Upcoming · local events</span>
            <div className="m-region-events-head">
              <h2 className="rec-h2">Professional events in <em>{meta.stateLabel || meta.label}</em>.</h2>
              <Link to={`/events?city=${encodeURIComponent({dmv:"Washington DC",nh:"New Hampshire",ma:"Massachusetts"}[slug]||"")}`} className="m-btn-ghost" data-testid="region-events-viewall">Browse all events →</Link>
            </div>
            <p className="rec-desc" style={{marginTop:-4, marginBottom:16, maxWidth:600}}>
              Networking, startup, and professional-development events near you. Every event links straight to the organizer.
            </p>
            <div className="m-events-grid">
              {upcomingEvents.slice(0, 3).map(e => <EventCard key={e.event_id} ev={e} compact />)}
            </div>
          </section>
        )}
      </main>
      <footer className="rec-footer">
        <span>© Métier · workmetier.app</span>
        <span><Link to="/nh" style={{color:"var(--gold)"}}>New Hampshire</Link> · <Link to="/ma" style={{color:"var(--gold)"}}>Massachusetts</Link></span>
      </footer>

      {returnPrompt && (
        <FeedbackPrompt
          workspaceSlug={returnPrompt.slug}
          workspaceName={returnPrompt.name}
          context="external_return"
          askBooked={true}
          visible={true}
          onDismiss={dismissReturnPrompt}
        />
      )}
    </div>
  );
}
